import { ArrowLeft, UsersThree } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../chat.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { PullToRefresh } from '@/features/discovery/components/pull-to-refresh'
import { useIsDesktop } from '@/features/discovery/hooks/use-is-desktop'
import { useOnlineStatus } from '@/lib/ui/use-online-status'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import type { PersonNameParts } from '@/lib/users/person-name'
import { ApiError } from '@/lib/api'

import { chatApi, type ActivityMessage } from '../api'
import { ChatComposer } from '../components/chat-composer'
import { ChatConfirmDialog } from '../components/chat-confirm-dialog'
import { ChatMembersSheet } from '../components/chat-members-sheet'
import { ChatMenu } from '../components/chat-menu'
import { ChatNotice, type ChatNoticeState } from '../components/chat-notice'
import {
  ChatRefreshButton,
  ChatRefreshStatus,
} from '../components/chat-refresh'
import { ChatTimeline, type ChatTimelineItem } from '../components/chat-timeline'
import {
  chatErrorKey,
  isActivityArchived,
  isActivityChatReadOnly,
  latestActivityMessageId,
} from '../format'
import { useActivityChat } from '../hooks/use-activity-chat'
import { useChatViewportLock } from '../hooks/use-chat-viewport-lock'
import { activityDraftKey, useChatUiStore } from '../state/chat-ui-store'

const MAX_ACTIVITY_MESSAGE = 1000

function toTimelineItem(
  message: ActivityMessage,
  canDeleteOthers: boolean,
  // D-101: author names follow the viewer's friendship with the sender.
  formatPerson: (person: PersonNameParts | null | undefined) => string,
): ChatTimelineItem {
  return {
    id: message.id,
    createdAt: message.createdAt,
    kind: message.type === 'system' ? 'system' : 'user',
    body: message.body,
    authorName:
      message.type === 'system' || !message.senderId
        ? message.senderName
        : formatPerson({ userId: message.senderId, publicName: message.senderName }),
    authorAvatarUrl: message.senderAvatarUrl,
    senderId: message.senderId,
    mine: message.mine,
    canDelete:
      message.type === 'user_message' &&
      (message.mine || canDeleteOthers),
  }
}

/**
 * `/chat/:id?`: activity group chat. Timeline includes user messages and the
 * synthesized join system items; writes are refused after `end_at + 7 days`.
 */
export function ActivityChatScreen() {
  const { id } = useParams()
  if (!id) return <Navigate to="/chats" replace />
  return <ActivityChatView key={id} activityId={id} />
}

function ActivityChatView({ activityId }: { activityId: string }) {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const online = useOnlineStatus()
  useChatViewportLock()
  const formatPerson = usePersonNameFormatter()
  const {
    activity,
    summary,
    messages,
    phase,
    refreshing,
    error,
    refresh,
    setMessages,
    markRead,
  } = useActivityChat(activityId)

  const draftKey = activityDraftKey(activityId)
  const input = useChatUiStore((state) => state.drafts[draftKey] ?? '')
  const setInput = (value: string) => {
    useChatUiStore.getState().setDraft(draftKey, value)
  }
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState<ChatNoticeState | null>(null)
  const [membersOpen, setMembersOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ChatTimelineItem | null>(null)
  const [pinOverride, setPinOverride] = useState<boolean | null>(null)
  const [pinBusy, setPinBusy] = useState(false)
  const [forcedReadOnly, setForcedReadOnly] = useState(false)
  const [atBottom, setAtBottom] = useState(true)
  const bodyRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const atBottomRef = useRef(true)

  const latestMessageId = latestActivityMessageId(messages)
  const readOnly =
    forcedReadOnly ||
    isActivityChatReadOnly(activity?.endAt ?? null)
  const archived = summary?.archived ?? (activity ? isActivityArchived(activity) : false)
  const pinned = pinOverride ?? summary?.pinned ?? false
  const canDeleteOthers = activity?.viewerStatus === 'creator'

  useEffect(() => {
    if (phase !== 'ready' || !latestMessageId || !atBottom) return
    void markRead(latestMessageId)
  }, [phase, latestMessageId, atBottom, markRead])

  useEffect(() => {
    atBottomRef.current = atBottom
  }, [atBottom])

  useEffect(() => {
    if (messages.length === 0 || !atBottom) return
    bottomRef.current?.scrollIntoView?.({ block: 'end' })
  }, [messages.length, atBottom])

  useEffect(() => {
    const body = bodyRef.current
    const Observer = window.ResizeObserver
    if (!body || !Observer) return
    let frame = 0
    const observer = new Observer(() => {
      if (!atBottomRef.current) return
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        if (!atBottomRef.current) return
        // Pin directly rather than using scrollIntoView: the document has
        // smooth scrolling enabled, and scrollIntoView would animate the
        // timeline for seconds while the keyboard is already open.
        body.scrollTop = body.scrollHeight
      })
    })
    observer.observe(body)
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    const scrollBody = body
    function handleScroll() {
      const scrollBottom =
        scrollBody.scrollHeight - scrollBody.scrollTop - scrollBody.clientHeight <= 96
      setAtBottom(scrollBottom)
    }
    handleScroll()
    scrollBody.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollBody.removeEventListener('scroll', handleScroll)
  }, [])

  async function send() {
    if (sending) return
    const body = input.trim()
    if (!body) return
    if (body.length > MAX_ACTIVITY_MESSAGE) {
      setNotice({ messageKey: 'chat.activity.tooLong', tone: 'error' })
      return
    }
    // Flush the composer on the keypress (maintainer direction): the input
    // empties when Enter/send is pressed, not after the round trip, so fast
    // typists can never see stale text concatenated back into the field.
    useChatUiStore.getState().clearDraft(draftKey)
    setSending(true)
    try {
      const timeline = await chatApi.sendActivityMessage(activityId, body)
      setMessages(() => timeline.items)
      setAtBottom(true)
      setForcedReadOnly(false)
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'CHAT_READ_ONLY') {
        setForcedReadOnly(true)
      }
      // The field stays empty; the flushed text is restored so the user can
      // retry without retyping.
      useChatUiStore.getState().setDraft(draftKey, body)
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setSending(false)
    }
  }

  async function togglePin() {
    if (pinBusy) return
    setPinBusy(true)
    const next = !pinned
    try {
      await chatApi.pinActivityChat(activityId, next)
      setPinOverride(next)
      setNotice({
        messageKey: next ? 'chat.notice.pinned' : 'chat.notice.unpinned',
        tone: 'success',
      })
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setPinBusy(false)
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await chatApi.deleteActivityMessage(activityId, deleteTarget.id)
      const timeline = await chatApi.listActivityMessages(activityId)
      setMessages(() => timeline.items)
      setDeleteTarget(null)
      setNotice({ messageKey: 'chat.notice.messageDeleted', tone: 'success' })
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  const timelineItems = messages.map((message) =>
    toTimelineItem(message, canDeleteOthers, formatPerson),
  )
  const memberCount = activity?.currentParticipantsCount

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-labelledby="activity-chat-title"
      className="content-width chat-page"
    >
      <header className="chat-header">
        <Link to="/chats" className="chat-header__back">
          <ArrowLeft size={22} weight="bold" aria-hidden="true" />
          <span className="sr-only">{t('chat.activity.back')}</span>
        </Link>
        <div className="chat-header__titles">
          <h1 id="activity-chat-title" className="chat-header__title">
            {activity?.title ?? t('screens.chat.title')}
          </h1>
        </div>
        {activity ? (
          <button
            type="button"
            className="chat-header__members"
            onClick={() => setMembersOpen(true)}
            aria-label={t('chat.members.open', {
              count: memberCount ?? activity.currentParticipantsCount,
            })}
          >
            <UsersThree size={20} weight="bold" aria-hidden="true" />
          </button>
        ) : null}
        <ChatMenu
          items={[
            {
              key: 'pin',
              labelKey: pinned ? 'chat.actions.unpin' : 'chat.actions.pin',
              onSelect: () => void togglePin(),
              disabled: pinBusy,
            },
          ]}
          label={t('chat.actions.options')}
        />
        {isDesktop ? (
          <ChatRefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </header>

      <ChatNotice notice={notice} onDismiss={() => setNotice(null)} />
      {phase === 'ready' && error ? (
        <p className="chat-notice chat-notice--error" role="alert">
          {t(chatErrorKey(error, {privateChat: false}))}
        </p>
      ) : null}

      <PullToRefresh
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
        contained
        scrollRef={bodyRef}
        className="chat-page__body"
      >
        <ChatRefreshStatus visible={refreshing} />
        {archived && !readOnly ? (
          <p className="chat-archived-banner" role="status">
            {activity?.status === 'cancelled'
              ? t('chat.activity.cancelledNotice')
              : t('chat.activity.archivedNotice')}
          </p>
        ) : null}

        {phase === 'loading' ? (
          <LoadingState labelKey="chat.activity.loading" />
        ) : phase === 'error' ? (
          <ErrorState
            error={error}
            titleKey="chat.activity.errorTitle"
            onRetry={refresh}
          />
        ) : timelineItems.length === 0 ? (
          <p className="chat-empty" role="status">
            {t('chat.activity.empty')}
          </p>
        ) : (
          <>
            <ChatTimeline
              items={timelineItems}
              deletingId={deletingId}
              onDelete={setDeleteTarget}
            />
            <div ref={bottomRef} />
          </>
        )}
      </PullToRefresh>

      {phase === 'ready' ? (
        <div className="chat-composer-dock" data-keyboard-sticky>
          <ChatComposer
            value={input}
            onChange={setInput}
            onSend={() => void send()}
            maxLength={MAX_ACTIVITY_MESSAGE}
            hintKey="chat.activity.composerHint"
            sending={sending}
            readOnly={readOnly}
            disabled={!online}
          />
        </div>
      ) : null}

      {activity ? (
        <ChatMembersSheet
          activityId={activity.id}
          open={membersOpen}
          onClose={() => setMembersOpen(false)}
        />
      ) : null}

      <ChatConfirmDialog
        open={deleteTarget !== null}
        titleKey="chat.confirm.message.title"
        bodyKey="chat.confirm.message.activityBody"
        confirmLabelKey="chat.confirm.delete"
        busy={deletingId !== null}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </main>
  )
}
